import { apiT2B } from "@/lib/axios";

export type ConversationsCountResponse = {
  currentPeriodConversationCount: number;
  previousPeriodConversationCount: number;
  percentageIncrease: number;
};

export type InteractionsCountResponse = {
  currentPeriodConversationInteration: number;
  previousPeriodConversationInteration: number;
  percentageIncrease: number;
};

export type AverageInteractionsPerConversationResponse = {
  currentPeriodAverageConversationInteration: number;
  previousPeriodAverageConversationInteration: number;
  percentageIncrease: number;
};

export type AgentOfTheMonthResponse = {
  agent: string;
  conversationCount: number;
}[];

export type attendantPerformanceResponse = {
  attendant: string;
  monthlyAttendant: {
    year: string;
    month: number;
    monthName: string;
    attendantCount: number;
  }[];
}[];

export type OperationalKpi = {
  value: number;
};

export type OperationalNamedCount = {
  name: string;
  count: number;
};

export type OperationalQueueCapacity = {
  name: string;
  attendants: number;
  openConversations: number;
  conversationsPerAttendant: number;
};

export type OperationalBacklogBucket = {
  range: string;
  total: number;
  percentage: number;
};

export type OperationalHourlyCount = {
  hour: number;
  count: number;
};

export type OperationalDashboardResponse = {
  activeConversations: OperationalKpi;
  newConversationsToday: OperationalKpi;
  closedConversationsToday: OperationalKpi;
  unansweredConversations: OperationalKpi;
  unreadMessages: OperationalKpi;
  firstResponseTimeSeconds: OperationalKpi;
  responseSlaPercent: OperationalKpi;
  averageHandlingTimeSeconds: OperationalKpi;
  backlog: OperationalBacklogBucket[];
  conversationsByQueue: OperationalNamedCount[];
  queueCapacity: OperationalQueueCapacity[];
  conversationsByWorkflow: OperationalNamedCount[];
  conversationsByAttendant: OperationalNamedCount[];
  unreadByQueue: OperationalNamedCount[];
  aiVsHuman: OperationalNamedCount[];
  conversationsByHour: OperationalHourlyCount[];
};

export type OperationalDashboardFilters = {
  queueIds?: number[];
  workflowIds?: number[];
  tagNames?: string[];
};

export type ManagerialKpi = {
  value: number | null;
  previousValue: number | null;
  variation: number | null;
  isPointVariation: boolean;
  series: (number | null)[];
};

export type ManagerialDailyPoint = {
  date: string;
  newConversations: number;
  closedConversations: number;
  aiConversations: number;
  humanConversations: number;
  slaPercent: number | null;
  firstResponseTimeSeconds: number | null;
  averageHandlingTimeSeconds: number | null;
  csat: number | null;
  resolvedConversations: number;
  resolutionRatePercent: number | null;
};

export type ManagerialWeekdayCount = {
  weekday: number;
  count: number;
};

export type ManagerialQueueSla = {
  name: string;
  slaMinutes: number;
  slaPercent: number | null;
  previousSlaPercent: number | null;
  targetPercent: number;
  variation: number | null;
};

export type ManagerialQueueUnread = {
  rank: number;
  name: string;
  count: number;
  previousCount: number;
  variation: number | null;
};

export type ManagerialTagCount = {
  name: string;
  count: number;
};

export type ManagerialDashboardResponse = {
  year: number;
  month: number;
  periodStart: string;
  periodEnd: string;
  previousPeriodStart: string;
  previousPeriodEnd: string;
  slaTargetPercent: number;
  csatTarget: number;
  newConversations: ManagerialKpi;
  closedConversations: ManagerialKpi;
  responseSlaPercent: ManagerialKpi;
  firstResponseTimeSeconds: ManagerialKpi;
  averageHandlingTimeSeconds: ManagerialKpi;
  csat: ManagerialKpi;
  aiSharePercent: ManagerialKpi;
  attendedContacts: ManagerialKpi;
  newContacts: ManagerialKpi;
  aiConversations: number;
  humanConversations: number;
  daily: ManagerialDailyPoint[];
  entriesByHour: OperationalHourlyCount[];
  /** Total de entradas dentro do horário de atendimento. */
  entriesInsideBusinessHours?: number | ManagerialKpi | null;
  /** Total de entradas fora do horário de atendimento. */
  entriesOutsideBusinessHours?: number | ManagerialKpi | null;
  entriesByWeekday: ManagerialWeekdayCount[];
  backlog: OperationalBacklogBucket[];
  slaByQueue: ManagerialQueueSla[];
  unreadByQueue: ManagerialQueueUnread[];
  contactsByTag: ManagerialTagCount[];
  resolutionRatePercent: ManagerialKpi;
  resolvedConversations: ManagerialKpi;
  conversationsBySubject: ManagerialTagCount[];
  contactsRanking: ManagerialContactRank[];
};

export type ManagerialContactRank = {
  rank: number;
  contactId: number;
  name: string;
  count: number;
};

export type ManagerialDashboardFilters = OperationalDashboardFilters & {
  year?: number;
  month?: number;
  /** Inclusive calendar day (YYYY-MM-DD). Must be sent together with endDate. */
  startDate?: string;
  /** Inclusive calendar day (YYYY-MM-DD). Must be sent together with startDate. */
  endDate?: string;
  userIds?: number[];
};

class ServiceDashboardService {
  private baseUrl = "api/v1/servicedashboard";

  async conversationsCount(
    startDate: string,
    endDate: string
  ): Promise<ConversationsCountResponse> {
    const response = await apiT2B.get<ConversationsCountResponse>(
      `${this.baseUrl}/conversationcount?startDate=${startDate}&endDate=${endDate}`
    );
    return response.data;
  }

  async interactionsCount(startDate: string, endDate: string): Promise<InteractionsCountResponse> {
    const response = await apiT2B.get<InteractionsCountResponse>(
      `${this.baseUrl}/conversationinteraction?startDate=${startDate}&endDate=${endDate}`
    );
    return response.data;
  }

  async averageInteractionsPerConversation(
    startDate: string,
    endDate: string
  ): Promise<AverageInteractionsPerConversationResponse> {
    const response = await apiT2B.get<AverageInteractionsPerConversationResponse>(
      `${this.baseUrl}/averageconversationinteraction?startDate=${startDate}&endDate=${endDate}`
    );
    return response.data;
  }

  async monthlyServiceEvolution(
    startDate: string,
    endDate: string
  ): Promise<AgentOfTheMonthResponse> {
    const response = await apiT2B.get<AgentOfTheMonthResponse>(
      `${this.baseUrl}/agentsofthemonth?startDate=${startDate}&endDate=${endDate}`
    );
    return response.data;
  }

  async attendantPerformance(
    startDate: string,
    endDate: string
  ): Promise<attendantPerformanceResponse> {
    const response = await apiT2B.get<attendantPerformanceResponse>(
      `${this.baseUrl}/attendantperformance?startDate=${startDate}&endDate=${endDate}`
    );

    return response.data;
  }

  async operational(
    filters: OperationalDashboardFilters = {}
  ): Promise<OperationalDashboardResponse> {
    const response = await apiT2B.get<OperationalDashboardResponse>(`${this.baseUrl}/operational`, {
      params: {
        queueIds: filters.queueIds,
        workflowIds: filters.workflowIds,
        tagNames: filters.tagNames,
      },
      paramsSerializer: {
        indexes: null,
      },
    });
    return response.data;
  }

  async managerial(filters: ManagerialDashboardFilters = {}): Promise<ManagerialDashboardResponse> {
    const response = await apiT2B.get<ManagerialDashboardResponse>(`${this.baseUrl}/managerial`, {
      params: {
        year: filters.year,
        month: filters.month,
        startDate: filters.startDate && filters.endDate ? filters.startDate : undefined,
        endDate: filters.startDate && filters.endDate ? filters.endDate : undefined,
        queueIds: filters.queueIds,
        userIds: filters.userIds,
        workflowIds: filters.workflowIds,
        tagNames: filters.tagNames,
      },
      paramsSerializer: {
        indexes: null,
      },
    });
    return response.data;
  }
}

const serviceDashboardService = new ServiceDashboardService();
export default serviceDashboardService;
